import { messageAPI } from '@/services-ts/axios'
import type {
  ICreateNotificationTemplate,
  IGetMessageReport,
  IGetNotificationTemplateLocaleList,
  ISendCustomPersonalMessage,
  ISendNotification,
  ISetNotificationSettingActive,
  IUpdateNotificationTemplate,
  IUpdateNotificationTemplateLocale,
} from '@/types/Request'
import type { INotificationStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

export const useNotificationStore = defineStore('notification', {
  state: (): INotificationStore => ({
    notificationSettings: [],
    notificationTemplates: [],
    notificationTemplatesLocale: [],
    notificationSettingTemplates: [],
  }),

  actions: {
    async GetNotificationSettingList(ParentId: string | null = null) {
      const { status, data } =
        await messageAPI.NotificationServices.GetNotificationSettingList(ParentId)
      this.notificationSettings = status ? data : []
    },

    async SetNotificationSettingActive(payload: ISetNotificationSettingActive) {
      const { status } = await messageAPI.NotificationServices.SetNotificationSettingActive(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
    },

    async GetNotificationSettingTemplateList(NotificationSettingId: string) {
      const { status, data } =
        await messageAPI.NotificationServices.GetNotificationSettingTemplateList(
          NotificationSettingId,
        )

      this.notificationSettingTemplates = status ? data : []
    },

    async GetNotificationTemplateList(ParentId: string | null = null) {
      const { status, data } =
        await messageAPI.NotificationServices.GetNotificationTemplateList(ParentId)
      this.notificationTemplates = status ? data : []
    },

    async UpdateNotificationTemplate(payload: IUpdateNotificationTemplate) {
      const { status } = await messageAPI.NotificationServices.UpdateNotificationTemplate(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
    },

    async CreateNotificationTemplate(payload: ICreateNotificationTemplate) {
      const { status } = await messageAPI.NotificationServices.CreateNotificationTemplate(payload)

      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
        await this.GetNotificationTemplateList(payload.ParentId)
      }

      return status
    },

    async GetNotificationTemplateLocaleList(payload: IGetNotificationTemplateLocaleList) {
      const { status, data } =
        await messageAPI.NotificationServices.GetNotificationTemplateLocaleList(payload)

      this.notificationTemplatesLocale = status ? data : []
    },

    async UpdateNotificationTemplateLocale(payload: IUpdateNotificationTemplateLocale) {
      const { status } =
        await messageAPI.NotificationServices.UpdateNotificationTemplateLocale(payload)

      if (status) {
        const index = this.notificationTemplatesLocale.findIndex(
          item => item.id === payload.NotificationTemplateLocaleId,
        )

        if (index !== -1 && this.notificationTemplatesLocale[index]) {
          this.notificationTemplatesLocale[index] = {
            ...this.notificationTemplatesLocale[index],
            detail: payload.Detail,
            title: payload.Title,
          }
        }

        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async SendNotification(payload: ISendNotification) {
      const data = await messageAPI.NotificationServices.SendNotification(payload)

      if (data?.status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }

      if ((data as any).Message === 'limit') {
        void Alert({
          message: Format('admin_components_sendnotificaion_ratelimit'),
          type: 'danger',
        })
      }
    },

    async SendCustomPersonalMessage(payload: ISendCustomPersonalMessage) {
      const data = await messageAPI.MessageServices.SendCustomPersonalMessage(payload)

      if (data?.status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return data.status
    },

    async GetMessageList(payload: IGetMessageReport) {
      const { status, data, total } = await messageAPI.MessageServices.GetMessageList(payload)
      return status
        ? { data: data || [], total: total || 0, status }
        : { data: [], total: 0, status: false }
    },

    async GetMessageListExcel(data: IGetMessageReport) {
      await messageAPI.MessageServices.GetMessageListExcel(data)
    },

    RESET_TEMPLATE_LOCALE() {
      this.notificationTemplatesLocale = []
    },

    $RESET() {
      this.notificationSettings = []
      this.notificationTemplates = []
      this.notificationTemplatesLocale = []
    },
  },
})
